<template>
  <!-- same wrapper styles you used -->
  <div
    v-if="safe_questions.length"
    class="mt-3 ms-4 ps-3 border-start border-2 border-primary"
  >
    <div
      v-for="(dq, dq_index) in safe_questions"
      :key="dq.node_key || dq.model || dq.section_question_id || dq.id || dq_index"
      v-show="dq.is_visible"
      class="mb-3 rounded"
    >
      <!-- Question Label (same as yours) -->
      <label :for="dq.model" class="fw-medium text-xs text-start d-block mt-2">
        {{ dq_label(dq) }}
        <span v-if="dq_required(dq)" class="text-danger fs-5">*</span>
      </label>

      <!-- TEXT -->
      <input
        v-if="dq.type === 'text'"
        type="text"
        class="form-control form-control-lg text-xxs bg-light border-1 py-1 rounded-lg"
        :class="{ 'border-danger': submit_attempted && validation_errors[dq.model] }"
        :id="dq.model"
        :value="form_data[dq.model] || ''"
        @input="set_field(dq.model, $event.target.value, dq)"
        :placeholder="dq_label(dq)"
        :required="dq_required(dq) && dq.is_visible"
      />

      <!-- TEXTAREA -->
      <textarea
        v-else-if="dq.type === 'textarea'"
        class="form-control form-control-lg text-xxs bg-light border-1 py-1 rounded-lg"
        :class="{ 'border-danger': submit_attempted && validation_errors[dq.model] }"
        :id="dq.model"
        :value="form_data[dq.model] || ''"
        @input="set_field(dq.model, $event.target.value, dq)"
        :rows="3"
        :placeholder="dq_label(dq)"
        :required="dq_required(dq) && dq.is_visible"
      ></textarea>

      <!-- NUMBER -->
      <input
        v-else-if="dq.type === 'number'"
        type="text"
        class="form-control form-control-lg text-xxs bg-light border-1 py-1 rounded-lg"
        :class="{ 'border-danger': submit_attempted && validation_errors[dq.model] }"
        :id="dq.model"
        :value="form_data[dq.model] || ''"
        @input="on_number_input($event, dq)"
        :placeholder="dq_label(dq)"
        :required="dq_required(dq) && dq.is_visible"
      />

      <!-- DATE -->
      <input
        v-else-if="dq.type === 'date'"
        type="date"
        class="form-control form-control-lg text-xxs bg-light border-1 py-1 rounded-lg"
        :class="{ 'border-danger': submit_attempted && validation_errors[dq.model] }"
        :id="dq.model"
        :value="form_data[dq.model] || ''"
        @input="set_field(dq.model, $event.target.value, dq)"
        :required="dq_required(dq) && dq.is_visible"
      />

      <!-- FILE -->
      <div v-else-if="dq.type === 'file'">
        <div v-if="form_data[dq.model]">
          <div class="d-flex align-items-center gap-2">
            <span class="text-xs">{{ form_data[dq.model].file_name }}</span>

            <button
              class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark"
              type="button"
              @click="$emit('preview-file', dq.model)"
            >
              Preview
            </button>

            <button
              class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-white ms-2"
              type="button"
              @click="$emit('remove-file', dq.model)"
            >
              Remove
            </button>
          </div>
        </div>

        <div v-else>
          <input
            type="file"
            class="form-control form-control-lg text-xxs bg-light border-1 py-1 rounded-lg"
            :id="dq.model"
            @change="$emit('file-upload', $event, dq.model, true)"
            :required="dq_required(dq)"
          />

          <div v-if="file_validation_error?.[dq.model]" class="text-danger text-xxs mt-1">
            {{ file_validation_error[dq.model] }}
          </div>

          <div v-if="!form_data[dq.model]?.file" class="mt-2">
            <label class="text-xs" :for="dq.model + '_file_comment'">
              Please explain why you cannot upload this file:
            </label>

            <!--comment field WITHOUT v-model mutation -->
            <textarea
              :id="dq.model + '_file_comment'"
              class="form-control form-control-lg text-xs bg-light border-1 py-1 rounded-lg"
              :value="form_data[dq.model + '_file_comment'] || ''"
              @input="set_field(dq.model + '_file_comment', $event.target.value, dq)"
              rows="2"
              placeholder="Provide a reason..."
            ></textarea>
          </div>
        </div>
      </div>

      <!-- RADIO -->
      <div
        v-else-if="dq.type === 'single_select' || dq.type === 'radio'"
        class="radio-group mt-2 p-2 rounded"
        :class="{ 'border border-danger': submit_attempted && validation_errors[dq.model] }"
      >
        <div
          v-for="(option, opt_index) in (dq.options || [])"
          :key="'dq-radio-' + opt_index"
          class="form-check"
        >
          <input
            class="form-check-input"
            type="radio"
            :name="dq.model"
            :id="`${dq.model}-${opt_index}`"
            :checked="is_checked_radio(dq.model, option)"
            @change="set_field(dq.model, option.value || option.text, dq, true)"
            :required="dq_required(dq) && dq.is_visible"
          />
          <label class="form-check-label text-xxs">
            {{ option.text }}
          </label>
        </div>
      </div>

      <!-- CHECKBOX -->
      <div
        v-else-if="dq.type === 'multi_select' || dq.type === 'checkboxes'"
        class="mt-2 p-2 rounded"
        :class="{ 'border border-danger': submit_attempted && validation_errors[dq.model] }"
      >
        <div
          v-for="(option, opt_index) in (dq.options || [])"
          :key="'dq-checkbox-' + opt_index"
          class="form-check"
        >
          <input
            class="form-check-input"
            type="checkbox"
            :id="`${dq.model}-${opt_index}`"
            :checked="is_checked_checkbox(dq.model, option)"
            @change="toggle_checkbox(dq, option)"
          />
          <label class="form-check-label text-xxs">
            {{ option.text }}
          </label>
        </div>
      </div>

      <!-- FALLBACK -->
      <input
        v-else
        type="text"
        class="form-control form-control-lg text-xxs bg-light border-1 py-1 rounded-lg"
        :class="{ 'border-danger': submit_attempted && validation_errors[dq.model] }"
        :id="dq.model"
        :value="form_data[dq.model] || ''"
        @input="set_field(dq.model, $event.target.value, dq)"
        :placeholder="dq_label(dq)"
        :required="dq_required(dq) && dq.is_visible"
      />

      <!-- ERROR MESSAGE -->
      <div
        v-if="submit_attempted && validation_errors[dq.model]"
        class="text-danger text-xxs mt-1"
      >
        {{ validation_errors[dq.model] }}
      </div>

      <RecursiveDependentQuestions
        v-if="children(dq).length"
        :questions="children(dq)"
        :form_data="form_data"
        :validation_errors="validation_errors"
        :submit_attempted="submit_attempted"
        :file_validation_error="file_validation_error"
        :update_dependent_questions_visibility="update_dependent_questions_visibility"
        @update-field="$emit('update-field', $event)"
        @number-input="$emit('number-input', $event.event, $event.node)"
        @file-upload="$emit('file-upload', $event)"
        @preview-file="$emit('preview-file', $event)"
        @remove-file="$emit('remove-file', $event)"
      />
    </div>
  </div>
</template>

<script>
export default {
  name: "RecursiveDependentQuestions",
  props: {
    questions: { type: Array, default: () => [] },
    form_data: { type: Object, required: true }, // still a prop, but we never mutate it
    validation_errors: { type: Object, default: () => ({}) },
    file_validation_error: { type: Object, default: () => ({}) },
    submit_attempted: { type: Boolean, default: false },
    update_dependent_questions_visibility: { type: Function, default: null },
  },
  emits: ["update-field", "number-input", "file-upload", "preview-file", "remove-file"],
  computed: {
    safe_questions() {
      return Array.isArray(this.questions) ? this.questions.filter(Boolean) : [];
    },
  },
  methods: {
    children(node) {
      const list = node?.dependent_questions || node?.dependant_questions || [];
      return Array.isArray(list) ? list.filter(Boolean) : [];
    },
    dq_label(dq) {
      return dq?.label || dq?.question_text || "Question";
    },
    dq_required(dq) {
      return (dq?.required ?? dq?.is_mandatory) === true;
    },

    //single way to update parent data
    set_field(key, value, node = null, trigger_visibility = false) {
      this.$emit("update-field", { key, value });

      // if you want visibility logic to react on dependent answers too
      if (trigger_visibility && typeof this.update_dependent_questions_visibility === "function" && node) {
        this.update_dependent_questions_visibility(node, value);
      }
    },

    on_number_input(event, node) {
      // let parent sanitize value (your existing handleNumberInput)
      this.$emit("number-input", { event, node });
      // after parent sanitizes and sets, you're good
    },

    is_checked_radio(model, option) {
      const val = this.form_data?.[model];
      const optVal = option?.value || option?.text;
      return val === optVal;
    },

    is_checked_checkbox(model, option) {
      const arr = this.form_data?.[model];
      const optVal = option?.value || option?.text;
      return Array.isArray(arr) && arr.includes(optVal);
    },

    toggle_checkbox(dq, option) {
      const model = dq.model;
      const optVal = option?.value || option?.text;
      const current = this.form_data?.[model];
      const next = Array.isArray(current) ? [...current] : [];

      const idx = next.indexOf(optVal);
      if (idx >= 0) next.splice(idx, 1);
      else next.push(optVal);

      this.set_field(model, next, dq, true);
    },
  },
};
</script>
